import { SCENE } from './palette.js';

// Pixel art, authored as pixels.
//
// Every sprite below is a block of characters and a key saying what colour
// each character is, which is how this art was actually made on the hardware
// and is the only form of it that stays editable. A designer changing the
// cat's tail edits the cat.
//
// They compile to an SVG data URI used as a CSS background-image rather than
// to a canvas or to a pile of divs, for three reasons: the browser rasterises
// each one once however many times it appears, an SVG rect grid upscales to
// exact squares under image-rendering: pixelated, and a data URI needs no
// network, which matters on a display that has been switched on since March.

const DOT = '.'; // transparent

// Runs of one colour become one rect. A 30x26 window is 780 pixels and 41
// rects, and the difference is the whole cost of drawing several dozen props
// on every frame.
export function compile(rows, key) {
  const height = rows.length;
  const width = rows[0].length;
  // Ragged art is always a miscount rather than an intention, and it used to
  // render as a sprite silently missing its right-hand edge.
  const ragged = rows.findIndex((row) => row.length !== width);
  if (ragged >= 0) {
    throw new Error(`sprite row ${ragged} is ${rows[ragged].length} wide, not ${width}`);
  }
  const rects = [];

  for (let y = 0; y < height; y++) {
    const row = rows[y];
    let x = 0;
    while (x < row.length) {
      const ch = row[x];
      let run = 1;
      while (x + run < row.length && row[x + run] === ch) run++;
      if (ch !== DOT) {
        const fill = key[ch];
        if (!fill) throw new Error(`sprite uses ${ch}, which the key does not define`);
        rects.push(`<rect x="${x}" y="${y}" width="${run}" height="1" fill="${fill}"/>`);
      }
      x += run;
    }
  }

  // shape-rendering is belt and braces next to the integer grid: it stops a
  // fractional device-pixel ratio putting a seam between two rects that share
  // an edge.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" `
    + `viewBox="0 0 ${width} ${height}" shape-rendering="crispEdges">${rects.join('')}</svg>`;

  return {
    width,
    height,
    // encodeURIComponent rather than base64: it is smaller for this kind of
    // markup and it leaves the sprite readable in devtools.
    url: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
  };
}

// Several frames of one sprite, side by side in one image, for something that
// animates in place - a candle, a spider's legs. CSS steps through them with
// background-position, so the animation costs no JavaScript and no React
// render however many of them are on the level. `width` is one frame's.
export function strip(frames, key) {
  const rows = frames[0].map((_, y) => frames.map((frame) => frame[y]).join(''));
  const sheet = compile(rows, key);
  return { ...sheet, width: sheet.width / frames.length, frames: frames.length };
}

// The style a sprite is drawn with. Sizes are in world pixels; the stage's
// own transform is what turns those into screen pixels.
export const spriteStyle = (sprite, { x, bottom }) => ({
  position: 'absolute',
  left: `${x}px`,
  bottom: `${bottom}px`,
  width: `${sprite.width}px`,
  height: `${sprite.height}px`,
  backgroundImage: sprite.url,
  backgroundSize: '100% 100%',
  imageRendering: 'pixelated',
});

// The reader, 24x52, facing right - the level flips the element rather than
// carrying mirrored copies of the art.
//
// A side-scroller needs somebody in it. Without the sprite the level is a
// photograph of some bookcases that happens to slide, and the whole reason for
// the mode is that it is not.
//
// He is assembled from a head, one of two torsos and one of five sets of legs,
// because six whole frames of a figure this size is a great deal of art to
// keep in step with itself - change the tunic once and every pose has it. The
// arms are in the torso and the torso cannot reach above the head's rows, so
// reaching for a book is an arm put out towards the shelf rather than over
// him. On a ladder that is what the arm does anyway.
const READER_KEY = {
  k: '#1c1c1c',
  h: '#881400', // hair
  s: '#f0d0b0', // skin
  t: '#0058f8', // tunic
  u: '#0000a8', // tunic shadow
  // Trousers. Grey rather than the brown they started as, which was the
  // floor's own colour to the byte - so the reader's legs measured 1.0:1
  // against the boards and simply were not there. This is 6.3:1.
  p: '#bcbcbc',
  b: '#f8b800', // the book being carried
  w: '#f8f8f8', // its pages
};

const HEAD = [
  '......kkkkkkkkkkkk......',
  '....kkhhhhhhhhhhhhkk....',
  '...khhhhhhhhhhhhhhhhk...',
  '...khhhhhhhhhhhhhhhhk...',
  '...khhhssssssssssshhk...',
  '...khhssssssssssssshk...',
  '...khsssssssssssssssk...',
  '...khsssskkssskkssssk...',
  '...khsssskkssskkssssk...',
  '...khsssssssssssssssk...',
  '...khssssssskkssssssk...',
  '...khsssssssssssssssk...',
  '....khssssssssssssshk...',
  '.....kssssssssssssk.....',
  '.......kssssssk.........',
  '.......kssssssk.........',
];

// Arms down, a book held against the chest.
const TORSO_HOLD = [
  '.....kkttttttttttkk.....',
  '...kkttttttttttttttkk...',
  '..kttttttttttttttttttk..',
  '..ktuttttttttttttttutk..',
  '..ktuttttttttttttttutk..',
  '..ktuttttttttttttttutk..',
  '..kttttttttttttttttttk..',
  '..kkttttttttttttttttkk..',
  '...kkbbbbbbbbbbbbbbkk...',
  '....kbbbbbbbbbbbbbbk....',
  '....kbbwwwwwwwwwwbbk....',
  '....kbbwwwwwwwwwwbbk....',
  '....kbbbbbbbbbbbbbbk....',
  '....kkbbbbbbbbbbbbkk....',
  '.....ktttttttttttttk....',
  '.....kttttttttttttk.....',
  '......kttttttttttk......',
  '......kttttttttttk......',
  '......kppppppppppk......',
  '......kppppppppppk......',
];

// The near arm out towards the shelf, and nothing in his hands - he is about
// to have the book, not holding it. Doubles as the climbing pose, where the
// arm is on a rung instead and the legs are what say which it is.
export const TORSO_REACH = [
  '.....kkttttttttttkk.....',
  '...kkttttttttttttttkk...',
  '..kttttttttttttttttttkkk',
  '..ktutttttttttttttttsssk',
  '..ktutttttttttttttttsssk',
  '..kttttttttttttttttttkkk',
  '..kkttttttttttttttttkk..',
  '...kkttttttttttttttkk...',
  '....kttttttttttttttk....',
  '....kttttttttttttttk....',
  '....kttttttttttttttk....',
  '....kttttttttttttttk....',
  '....kttttttttttttttk....',
  '....kkttttttttttttkk....',
  '.....ktttttttttttttk....',
  '.....kttttttttttttk.....',
  '......kttttttttttk......',
  '......kttttttttttk......',
  '......kppppppppppk......',
  '......kppppppppppk......',
];

const LEGS = {
  idle: [
    '......kppppppppppk......',
    '......kpppp..ppppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '......kppp....pppk......',
    '.....kkkkk..kkkkk.......',
    '.....kkkkk..kkkkk.......',
    '.....kkkk....kkkk.......',
  ],
  // Mid-stride, both legs under him.
  step1: [
    '......kppppppppppk......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '......kpppppppppk.......',
    '.....kkkkkkkkkkkk.......',
    '.....kkkkkkkkkkkk.......',
    '.....kkkkkkkkkk.........',
  ],
  // And the other half of it, one leg forward and one trailing.
  step2: [
    '......kppppppppppk......',
    '.....kpppp..ppppk.......',
    '....kppp.....pppk.......',
    '....kpp.......ppk.......',
    '...kppp.......ppk.......',
    '...kpp.........pk.......',
    '..kppp.........pk.......',
    '..kpp...........pk......',
    '..kpp...........pk......',
    '.kppp...........pk......',
    '.kpp.............pk.....',
    '.kpp.............pk.....',
    'kppp.............ppk....',
    'kkkk.............kkk....',
    'kkkk.............kkk....',
    'kkk...............kk....',
  ],
  // On a ladder: the near knee drawn up onto the next rung, then the far one.
  climb1: [
    '......kppppppppppk......',
    '......kpppp..ppppk......',
    '.....kppp.....pppk......',
    '....kppp......pppk......',
    '...kppp.......pppk......',
    '..kppp........pppk......',
    '..kpp.........pppk......',
    '..kpp.........pppk......',
    '..kppp........pppk......',
    '...kpp........pppk......',
    '...kpp........pppk......',
    '...kpp........pppk......',
    '...kpp........pppk......',
    '..kkkk........kkkk......',
    '..kkkk........kkkk......',
    '..kkk..........kkk......',
  ],
  climb2: [
    '......kppppppppppk......',
    '......kpppp..ppppk......',
    '......kppp.....pppk.....',
    '......kppp......pppk....',
    '......kppp.......pppk...',
    '......kppp........pppk..',
    '......kppp.........ppk..',
    '......kppp.........ppk..',
    '......kppp........pppk..',
    '......kppp........ppk...',
    '......kppp........ppk...',
    '......kppp........ppk...',
    '......kppp........ppk...',
    '......kkkk........kkkk..',
    '......kkkk........kkkk..',
    '......kkk..........kkk..',
  ],
};

// Every pose of a reader, from a head, a key and the torso he walks about in.
//
// A function rather than one constant so that a season can recast him - see
// seasons/ - by drawing a new head and choosing new colours, while the
// torsos and the legs, which are most of the art and all of the animation,
// stay the ones drawn here. Whatever comes out must be the size this one is:
// the level positions him by READER.idle.width and a wider stand-in would walk
// half a sprite off where it thinks he is.
export function castReader({ head = HEAD, key = READER_KEY, walk = TORSO_HOLD } = {}) {
  // A head of the wrong width is ragged art and compile() refuses it; the
  // wrong height would compile, and stand him taller than the ladders.
  if (head.length !== HEAD.length) throw new Error(`a reader's head is ${HEAD.length} rows, not ${head.length}`);
  const pose = (torso, legs) => compile([...head, ...torso, ...legs], key);
  return {
    idle: pose(walk, LEGS.idle),
    step1: pose(walk, LEGS.step1),
    step2: pose(walk, LEGS.step2),
    reach: pose(TORSO_REACH, LEGS.idle),
    climb1: pose(TORSO_REACH, LEGS.climb1),
    climb2: pose(TORSO_REACH, LEGS.climb2),
  };
}

export const READER = castReader();

const PLANT = compile([
  '...g..G....',
  '..gG.GgG.g.',
  '.gGgGGgGgG.',
  'gGGgGGGgGGg',
  '.gGGGGGGGg.',
  '..gGGGGGg..',
  '...gGGGg...',
  '....gGg....',
  '.....g.....',
  '.....g.....',
  '..ttttttt..',
  '..tTTTTTt..',
  '..tTTTTTt..',
  '...tTTTt...',
  '...tTTTt...',
  '....ttt....',
], { g: '#006800', G: '#00a800', t: '#881400', T: '#e45c10' });

const LAMP = compile([
  '.sssssss.',
  'ssSSSSSss',
  'sSSSSSSSs',
  '.sssssss.',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '....b....',
  '...bbb...',
  '..bbbbb..',
  '.bbbbbbb.',
  'kkkkkkkkk',
], { s: '#ac7c00', S: '#f8d878', b: '#7c7c7c', k: '#1c1c1c' });

const CAT = compile([
  '..k...k....',
  '..kk.kk....',
  '..kkkkk...k',
  '..keekk..kk',
  '..kkkkkkkkk',
  '.kkkkkkkkkk',
  '.kkkkkkkkkk',
  '.k.kk.kk.k.',
], { k: '#1c1c1c', e: '#00e8d8' });

const CRATE = compile([
  'wwwwwwwwww',
  'wWWWWWWWWw',
  'wWwwwwwwWw',
  'wWwWWWWwWw',
  'wWwWddWwWw',
  'wWwWddWwWw',
  'wWwWWWWwWw',
  'wWwwwwwwWw',
  'wWWWWWWWWw',
  'wwwwwwwwww',
], { w: '#3c0800', W: '#881400', d: '#e45c10' });

const RUG = compile([
  '.rrrrrrrrrrrrrrrrrrrrrrrr.',
  'rRRRRRRRRRRRRRRRRRRRRRRRRr',
  'rRppRppRppRppRppRppRppRRRr',
  'rRRRRRRRRRRRRRRRRRRRRRRRRr',
  '.rrrrrrrrrrrrrrrrrrrrrrrr.',
], { r: '#940084', R: '#d800cc', p: '#f8d878' });

// Props stand in the gaps between bookcases. Each declares how wide a gap it
// needs, so the level can put the rug where there is floor for it and the cat
// where there is not.
export const PROPS = [
  { name: 'plant', sprite: PLANT, needs: 16 },
  { name: 'lamp', sprite: LAMP, needs: 14 },
  { name: 'crate', sprite: CRATE, needs: 14 },
  { name: 'cat', sprite: CAT, needs: 14 },
  { name: 'rug', sprite: RUG, needs: 30 },
];

// Wall furniture, on the parallax layer. It is what makes the wall move at
// half speed visibly rather than theoretically - a flat wall slid at any speed
// looks identical to a flat wall standing still.
const WINDOW = compile([
  'wwwwwwwwwwwwwwwwwwwwwwwwwwwwww',
  'wWWWWWWWWWWWWWWWWWWWWWWWWWWWWw',
  'wWnnnnnnnnnnnnWWnnnnnnnnnnnnWw',
  'wWnnnnnnnnnnnnWWnnnnnnnnnnnnWw',
  'wWnnnnnnnnnnnnWWnnnnnns*nnnnWw',
  'wWnnnnn*nnnnnnWWnnnnnnnnnnnnWw',
  'wWnnnnnnnnnnnnWWnnnnnnnnnnnnWw',
  'wWnnnn*nnnnnnnWWnnnnnnnn*nnnWw',
  'wWnnnnnnnnnnnnWWnnnnnnnnnnnnWw',
  'wWnnnnnnnn*nnnWWnnnnnnnnnnnnWw',
  'wWnnnnnnnnnnnnWWnnn*nnnnnnnnWw',
  'wWnnnnnnnnnnnnWWnnnnnnnnnnnnWw',
  'wWWWWWWWWWWWWWWWWWWWWWWWWWWWWw',
  'wWWWWWWWWWWWWWWWWWWWWWWWWWWWWw',
  'wWnnnnnnnnnnnnWWnnnnnnnnnnnnWw',
  'wWnnnnn*nnnnnnWWnnnnnnnnnnnnWw',
  'wWnnnnnnnnnnnnWWnnnnnn*nnnnnWw',
  'wWnnnnnnnnnnnnWWnnnnnnnnnnnnWw',
  'wWnnn*nnnnnnnnWWnnnnnnnnnnnnWw',
  'wWnnnnnnnnnnnnWWnnnnnnnnnnnnWw',
  'wWnnnnnnnnnnnnWWnnnnn*nnnnnnWw',
  'wWnnnnnnnn*nnnWWnnnnnnnnnnnnWw',
  'wWWWWWWWWWWWWWWWWWWWWWWWWWWWWw',
  'wwwwwwwwwwwwwwwwwwwwwwwwwwwwww',
  '.wwwwwwwwwwwwwwwwwwwwwwwwwwww.',
  '..wwwwwwwwwwwwwwwwwwwwwwwwww..',
], { w: '#3c0800', W: '#881400', n: '#0c0c3c', s: '#7c7c7c', '*': '#f8f8f8' });

const PICTURE = compile([
  'fffffffffffff',
  'fBBBBBBBBBBBf',
  'fBsssssssssBf',
  'fBssssSssssBf',
  'fBsssSSSsssBf',
  'fBsssssssssBf',
  'fBsgsssssgsBf',
  'fBgGgsssgGgBf',
  'fBGGGgsgGGGBf',
  'fBdddddddddBf',
  'fBBBBBBBBBBBf',
  'fffffffffffff',
], { f: '#f8b800', B: '#503000', s: '#3cbcfc', S: '#f8d878', g: '#006800', G: '#00a800', d: '#2c1c00' });

// Height is not fixed here: the level hangs these in bands worked out from
// how tall the room turns out to be. See wallBands in Level.jsx.
export const WALL_PROPS = [
  { name: 'window', sprite: WINDOW },
  { name: 'picture', sprite: PICTURE },
];

// Hearts, for a rating. A five-star scale halves cleanly into ten halves, so
// a book rated 3.5 gets three full hearts and one half - which is exactly the
// shape the console games showed and the reason this is hearts and not stars.
const HEART_KEY = { k: '#1c1c1c', r: '#f83800', R: '#f87858', e: '#7c7c7c', E: '#bcbcbc' };
// 8 wide so the two halves are mirror images of each other. A 7-wide heart
// has a middle column that has to belong to one side, and a half-filled one
// then reads as lopsided rather than as half.
const heartRows = (l, r) => [
  '.kk..kk.',
  `k${l}${l}kk${r}${r}k`,
  `k${l}${l}${l}${r}${r}${r}k`,
  `.k${l}${l}${r}${r}k.`,
  `..k${l}${r}k..`,
  '...kk...',
];

export const HEARTS = {
  full: compile(heartRows('R', 'r'), HEART_KEY),
  half: compile(heartRows('R', 'e'), HEART_KEY),
  empty: compile(heartRows('E', 'e'), HEART_KEY),
};

// The marker in front of the zone name on the status bar. A diamond, because
// the font has no glyph for one and a character it cannot draw comes out as a
// blank box - which on a screen this deliberate reads as a fault.
export const MARKER = compile([
  '...k...',
  '..kbk..',
  '.kbBbk.',
  'kbBBBbk',
  '.kbBbk.',
  '..kbk..',
  '...k...',
], { k: '#1c1c1c', b: '#ac7c00', B: '#f8b800' });

// The floor and wall are tiled rather than drawn as flat colour, because a
// flat colour slid sideways is invisible and the parallax is the whole point.
// Sixteen wide by exactly the height of the floor, so it tiles sideways only.
// Tiling it vertically too would repeat the lit top edge down the middle of
// the floor, which reads as a wall rather than as boards seen edge-on.
export const FLOOR_TILE = compile([
  'bbbbbbbbbbbbbbbb',
  'BBBBBBBBBBBBBBBB',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'dddddddddddddddd',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'dddddddddddddddd',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'dddddddddddddddd',
], { b: SCENE.floorLit, B: SCENE.wood, f: SCENE.floor, d: SCENE.floorDark });

export const WALL_TILE = compile([
  'WWWWWWWWWWWWWWWW',
  'WwwwwwwWWwwwwwwW',
  'WwwwwwwWWwwwwwwW',
  'WwwwwwwWWwwwwwwW',
  'WwwwwwwWWwwwwwwW',
  'WWWWWWWWWWWWWWWW',
  'wwwWWwwwwwwwWWww',
  'wwwWWwwwwwwwWWww',
  'wwwWWwwwwwwwWWww',
  'wwwWWwwwwwwwWWww',
  'WWWWWWWWWWWWWWWW',
  'WwwwwwwWWwwwwwwW',
  'WwwwwwwWWwwwwwwW',
  'WwwwwwwWWwwwwwwW',
  'WwwwwwwWWwwwwwwW',
  'WWWWWWWWWWWWWWWW',
], { W: SCENE.wallDark, w: SCENE.wall });
